import { useMemo } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { formatTime } from '@/lib/format';
import { isSessionWet } from '@/lib/weather';
import { useI18n } from '@/context/I18nContext';
import type { LapRef, LapEntry, SessionSummary } from '@/types/telemetry';
export type { LapRef };

export interface SessionWithLaps extends SessionSummary {
  laps: LapEntry[];
  loading: boolean;
}

interface LapSelectorProps {
  sessions: SessionWithLaps[];
  expandedSessions: Set<string>;
  selectedLaps?: LapRef[];
  onToggleSession: (sessionId: string) => void;
  onToggleLap?: (sessionId: string, lapNumber: number) => void;
  activeSide?: 'A' | 'B';
  lapA?: LapRef | null;
  lapB?: LapRef | null;
  labelA?: string;
  labelB?: string;
  onSelectSideLap?: (side: 'A' | 'B', lap: LapRef | null) => void;
  driverFilter?: string;
  onDriverFilterChange?: (driver: string) => void;
  weatherFilter?: string;
  onWeatherFilterChange?: (filter: string) => void;
  uniqueDrivers?: string[];
}

export function LapSelector({
  sessions,
  expandedSessions,
  selectedLaps = [],
  onToggleSession,
  onToggleLap,
  activeSide,
  lapA,
  lapB,
  labelA,
  labelB,
  onSelectSideLap,
  driverFilter = 'all',
  onDriverFilterChange,
  weatherFilter = 'all',
  onWeatherFilterChange,
  uniqueDrivers = [],
}: LapSelectorProps) {
  const { t } = useI18n();

  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      if (driverFilter !== 'all' && s.driverName !== driverFilter) return false;
      if (weatherFilter === 'dry' && isSessionWet(s.weather)) return false;
      if (weatherFilter === 'wet' && !isSessionWet(s.weather)) return false;
      return true;
    });
  }, [sessions, driverFilter, weatherFilter]);

  const isSelectedLegacy = (sessionId: string, lapNumber: number) =>
    selectedLaps.some(l => l.sessionId === sessionId && l.lapNumber === lapNumber);

  const isSideMode = activeSide !== undefined && onSelectSideLap !== undefined;

  return (
    <div className="card overflow-hidden">
      {/* Header / Filters */}
      {onDriverFilterChange && onWeatherFilterChange ? (
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle bg-bg-surface/50">
          <select
            value={driverFilter}
            onChange={e => onDriverFilterChange(e.target.value)}
            className="flex-1 text-xs py-1.5 px-2.5 rounded-lg bg-bg-surface border border-border-subtle text-text-secondary focus:border-accent outline-none"
          >
            <option value="all">{t('sessionSelector.allDrivers')}</option>
            {uniqueDrivers.map(d => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            value={weatherFilter}
            onChange={e => onWeatherFilterChange(e.target.value)}
            className="flex-1 text-xs py-1.5 px-2.5 rounded-lg bg-bg-surface border border-border-subtle text-text-secondary focus:border-accent outline-none"
          >
            <option value="all">{t('sessionSelector.allWeather')}</option>
            <option value="dry">{t('sessionSelector.dryOnly')}</option>
            <option value="wet">{t('sessionSelector.wetOnly')}</option>
          </select>
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${
              activeSide === 'A'
                ? 'bg-red-500/15 border-red-400/40 text-red-300'
                : 'bg-blue-500/15 border-blue-400/40 text-blue-300'
            }`}
          >
            {activeSide === 'A' ? labelA || 'Side A' : labelB || 'Side B'}
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-bg-surface/50">
          <span className="text-[0.72rem] font-semibold uppercase tracking-widest text-text-secondary">
            {t('lapSelector.title')}
          </span>
          <span className="text-[0.68rem] text-text-muted bg-bg-surface border border-border-subtle rounded-full px-2 py-0.5 mono">
            {selectedLaps.length}/2
          </span>
        </div>
      )}

      {/* Session list */}
      <div className="max-h-[460px] overflow-y-auto">
        {filteredSessions.map(s => {
          const expanded = expandedSessions.has(s.id);
          const hasLapAInSession = lapA?.sessionId === s.id;
          const hasLapBInSession = lapB?.sessionId === s.id;
          return (
            <div key={s.id} className="border-b border-border-subtle last:border-b-0">
              <div
                onClick={() => onToggleSession(s.id)}
                className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-bg-hover transition-colors select-none"
              >
                <span className="text-text-muted w-3 flex-shrink-0">
                  {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <span className="text-[0.75rem] font-semibold text-text-primary flex-1 flex items-center gap-2">
                  <span>
                    {s.date} {s.time}
                  </span>
                  {hasLapAInSession && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-400/40">
                      {labelA || 'A'} · L{lapA?.lapNumber}
                    </span>
                  )}
                  {hasLapBInSession && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-400/40">
                      {labelB || 'B'} · L{lapB?.lapNumber}
                    </span>
                  )}
                </span>
                <span className="text-[0.7rem] text-text-muted">
                  {s.driverName} · {s.completedLaps}L
                </span>
              </div>

              {expanded && (
                <div className="bg-bg-surface/30">
                  {s.loading ? (
                    <div className="flex items-center gap-2 px-4 py-2 text-text-muted text-xs">
                      <div className="w-3 h-3 border-[1.5px] border-accent/30 border-t-accent rounded-full animate-spin" />
                      {t('lapSelector.loadingLaps')}
                    </div>
                  ) : s.laps.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-text-muted">{t('lapSelector.noLaps')}</div>
                  ) : (
                    s.laps.map(l => {
                      const lap = l.lap;
                      const isA = isSideMode && lapA?.sessionId === s.id && lapA?.lapNumber === lap.lapNumber;
                      const isB = isSideMode && lapB?.sessionId === s.id && lapB?.lapNumber === lap.lapNumber;
                      const selected = isSideMode
                        ? activeSide === 'A'
                          ? isA
                          : isB
                        : isSelectedLegacy(s.id, lap.lapNumber);

                      const handleLapClick = () => {
                        if (isSideMode && activeSide && onSelectSideLap) {
                          if (selected) {
                            onSelectSideLap(activeSide, null);
                          } else {
                            onSelectSideLap(activeSide, {
                              sessionId: s.id,
                              lapNumber: lap.lapNumber,
                              sessionDate: s.date,
                              sessionTime: s.time,
                              lapTimeMs: lap.lapTimeMs,
                            });
                          }
                        } else if (onToggleLap) {
                          onToggleLap(s.id, lap.lapNumber);
                        }
                      };

                      return (
                        <div
                          key={lap.lapNumber}
                          onClick={handleLapClick}
                          className={`flex items-center gap-2 px-4 pl-7 py-1.5 cursor-pointer transition-colors ${
                            isA
                              ? 'bg-red-500/15 hover:bg-red-500/20 border-l-2 border-l-red-500'
                              : isB
                                ? 'bg-blue-500/15 hover:bg-blue-500/20 border-l-2 border-l-blue-500'
                                : 'hover:bg-bg-hover'
                          }`}
                        >
                          <input
                            type={isSideMode ? 'radio' : 'checkbox'}
                            checked={selected}
                            onChange={handleLapClick}
                            className={`w-3.5 h-3.5 cursor-pointer shrink-0 ${
                              activeSide === 'B' ? 'accent-blue-500' : 'accent-red-500'
                            }`}
                            onClick={e => e.stopPropagation()}
                          />
                          <span className="text-[0.72rem] font-semibold text-text-secondary min-w-[36px]">
                            L{lap.lapNumber}
                          </span>
                          <span className="mono text-[0.72rem] text-text-primary flex-1">
                            {formatTime(lap.lapTimeMs)}
                          </span>
                          {isA && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/20 border border-red-400/40 rounded px-1.5 py-0.5">
                              {labelA || 'Side A'}
                            </span>
                          )}
                          {isB && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300 bg-blue-500/20 border border-blue-400/40 rounded px-1.5 py-0.5">
                              {labelB || 'Side B'}
                            </span>
                          )}
                          {l.isBest && (
                            <span className="text-[0.6rem] font-bold uppercase tracking-wider text-accent bg-accent/10 rounded px-1.5 py-0.5">
                              {t('lapSelector.best')}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
